import React, { useState } from 'react';
import * as R from 'ramda';
import { arrayOf, bool, func, number, oneOfType, shape, string } from 'prop-types';
import { capitalize } from 'utils/helpers/format';
import { findOptionByValue } from 'utils/helpers/function';
import { InfoOutlined } from '@mui/icons-material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { endOfDay, formatISO, isValid, parseISO } from 'date-fns/fp';
import { addDays, endOfTomorrow, isEqual, startOfToday } from 'date-fns';

import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Switch,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import CheckboxRoundedIcon from '@mui/icons-material/CheckBoxRounded';
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded';

import { MAutocomplete, MDropdown } from 'common';
import { EvaluateFlag } from 'common/FeatureFlags/Flag';

// Utils
import {
  EXPIRATION_MESSAGE,
  NO_END_DATE,
  SUPPLIER_AUTHORIZATION_TYPE_OPTIONS,
} from 'feature/loading-management/utils/const';
import { isLessThan, isNotEmpty, isNumber } from 'utils/helpers/formValidations';
import { theme } from 'library';
import {
  alertOutlinedWarningStyles,
  alertOutlinedWarningStylesAlt,
  alertRootStyles,
  alertRootStylesAlt,
  calendarContentStyles,
  contentStyles,
} from '../Steps.styles';

const authNumberError = 'Authorization Number is required and must be between 4 and 6 digits.';
const supplierAuthDateHelperText = 'End date must be between tomorrow and 12/31/2999';

const validateAuthNumber = R.cond([
  [R.isEmpty, R.always(authNumberError)],
  [value => !isNumber(value), R.always(authNumberError)],
  [value => isLessThan(value, 3), R.always(authNumberError)],
  [value => !isLessThan(value, 6), R.always(authNumberError)],
  [R.T, R.always('')],
]);

const VariableFields = ({
  authorizationBy,
  params,
  setParams,
  setHasUnsavedChanges,
  setWizardDetails,
  viewById,
  viewByListFetched,
  viewByListOptions,
}) => {
  const isCreateSupplierAuthorizationsEnabled = EvaluateFlag(
    process.env.REACT_APP_LD_CREATE_SUPPLIER_AUTHORIZATIONS
  );

  const { authorizationNumber, authorizationType, authorizedVolume, endDate, renewalVolume } =
    params;

  const selectedViewByListOption = findOptionByValue(viewById, viewByListOptions);

  const [hasDateValidationError, setHasDateValidationError] = useState(false);
  const [validation, setValidation] = useState('');
  const [hasEndDate, setHasEndDate] = useState(!isEqual(parseISO(endDate), NO_END_DATE));

  const noEndDate = !hasEndDate;

  const selectedDate = noEndDate ? endOfTomorrow() : parseISO(endDate);

  const tomorrow = addDays(startOfToday(), 1);

  const handleOnChange = ({ value }) => {
    setHasUnsavedChanges(true);
    if (R.either(R.equals('UNLIMITED'), R.equals('LIMITED'))(value)) {
      setParams({
        authorizationType: value,
        authorizedVolume: undefined,
        renewalVolume: undefined,
      });
      return;
    }

    setParams({ authorizationType: value });
  };

  const handleVolume = (name, value) => {
    setHasUnsavedChanges(true);
    setParams({ [name]: R.either(parseInt, R.always(''))(value) });
  };

  const handleSetNoEndDate = checked => {
    setHasUnsavedChanges(true);
    setHasEndDate(checked);
    setHasDateValidationError(false);
    if (!checked) {
      setParams({ endDate: formatISO(NO_END_DATE) });
    } else {
      setParams({ endDate: formatISO(endOfTomorrow()) });
    }
  };

  const handleSetNoEndDateLegacy = (_, checked) => {
    setHasUnsavedChanges(true);
    setHasEndDate(!checked);
    setHasDateValidationError(false);
    setParams({ endDate: formatISO(checked ? NO_END_DATE : endOfTomorrow()) });
  };

  const handleDateChange = date => {
    setHasUnsavedChanges(true);
    setParams({
      endDate:
        isValid(date) && date > tomorrow && date <= NO_END_DATE
          ? R.pipe(endOfDay, formatISO)(date)
          : '',
    });
  };

  const handleAuthorizationNumberChange = e => {
    setHasUnsavedChanges(true);
    const { value } = e.target;
    setParams({ authorizationNumber: value });
    setValidation(validateAuthNumber(value));
  };

  const handleDateError = error => {
    setHasDateValidationError(Boolean(error));
  };

  return (
    <Box sx={contentStyles}>
      {isCreateSupplierAuthorizationsEnabled ? (
        <>
          <Box sx={{ display: 'grid', rowGap: 2 }}>
            <MDropdown
              disableAutoLabelColor
              id="type"
              items={SUPPLIER_AUTHORIZATION_TYPE_OPTIONS}
              label="Type"
              name="authorizationType"
              onChange={handleOnChange}
              value={authorizationType ?? ''}
            />
            {authorizationType !== 'UNLIMITED' && (
              <TextField
                label="Authorization Volume (Gallons)"
                onChange={e => handleVolume('authorizedVolume', e.target.value.replace(/\D/g, ''))}
                value={authorizedVolume ? Number(authorizedVolume).toLocaleString('en-US') : ''}
              />
            )}
            {authorizationType !== 'UNLIMITED' && authorizationType !== 'LIMITED' && (
              <TextField
                label="Renewal Volume (Gallons)"
                onChange={e => handleVolume('renewalVolume', e.target.value.replace(/\D/g, ''))}
                value={renewalVolume ? Number(renewalVolume).toLocaleString('en-US') : ''}
              />
            )}
            <MAutocomplete
              hasSearched={viewByListFetched}
              id={authorizationBy}
              label={capitalize(authorizationBy)}
              loading={false}
              name="viewById"
              onChange={(_, val) => {
                setHasUnsavedChanges(true);
                setWizardDetails({ name: 'viewById', value: R.prop('value', val) });
              }}
              openOnFocus
              options={viewByListOptions}
              popupIcon={<ArrowDropDownIcon />}
              value={selectedViewByListOption?.value}
            />
            <TextField
              data-testid="authorization-number"
              error={isNotEmpty(validation)}
              helperText={validation || 'Must be between 4-6 digits'}
              label="Authorization Number"
              onChange={handleAuthorizationNumberChange}
              value={authorizationNumber ?? ''}
            />
          </Box>
        </>
      ) : (
        <>
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={noEndDate}
                  checkedIcon={<CheckboxRoundedIcon sx={{ color: 'secondary.main' }} />}
                  icon={<CheckBoxOutlineBlankRoundedIcon />}
                  name="noEndDate"
                  onChange={handleSetNoEndDateLegacy}
                  sx={{
                    '&.MuiCheckbox-root': {
                      borderRadius: '50%',
                      color: 'text.secondary',
                    },
                  }}
                />
              }
              label="No End Date"
            />
          </Box>
        </>
      )}
      {isCreateSupplierAuthorizationsEnabled && (
        <Box sx={{ px: 1 }}>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={hasEndDate}
                  onChange={(_, checked) => handleSetNoEndDate(checked)}
                />
              }
              label="Add End Date"
              sx={{ width: 'fit-content' }}
            />
          </FormGroup>
        </Box>
      )}
      {!noEndDate && (
        <Box sx={calendarContentStyles}>
          <DatePicker
            disablePast
            htmlFor="end-date"
            id="end-date"
            label={isCreateSupplierAuthorizationsEnabled ? 'Select Date' : 'End Date'}
            maxDate={NO_END_DATE}
            minDate={tomorrow}
            onChange={handleDateChange}
            onError={handleDateError}
            slotProps={{
              textField: {
                error: hasDateValidationError,
                helperText: isCreateSupplierAuthorizationsEnabled ? supplierAuthDateHelperText : '',
              },
            }}
            value={selectedDate}
          />
          <Alert
            // If this alert style is used elsewhere, consider moving these style overrides to theme
            icon={isCreateSupplierAuthorizationsEnabled ? <InfoOutlined /> : false}
            severity="warning"
            sx={{
              ...(isCreateSupplierAuthorizationsEnabled
                ? alertOutlinedWarningStyles(theme)
                : alertOutlinedWarningStylesAlt(theme)),
              ...(isCreateSupplierAuthorizationsEnabled
                ? alertRootStyles(theme)
                : alertRootStylesAlt(theme)),
            }}
            variant="outlined"
          >
            {isCreateSupplierAuthorizationsEnabled ? (
              <>
                This authorization will expire at <strong>11:59pm on the End Date</strong> specified
                and cannot be modified after it expires.
              </>
            ) : (
              EXPIRATION_MESSAGE
            )}
          </Alert>
        </Box>
      )}
      {!isCreateSupplierAuthorizationsEnabled && (
        <>
          <TextField
            data-testid="authorization-number"
            error={isNotEmpty(validation)}
            helperText={validation}
            label="Authorization Number"
            onChange={handleAuthorizationNumberChange}
            value={authorizationNumber ?? ''}
          />
          <MDropdown
            id="type"
            items={SUPPLIER_AUTHORIZATION_TYPE_OPTIONS}
            label="Type"
            marginBottom="73px"
            name="authorizationType"
            onChange={handleOnChange}
            value={authorizationType ?? ''}
          />
          {authorizationType !== 'UNLIMITED' && (
            <TextField
              label="Authorization Volume (Gallons)"
              onChange={e => handleVolume('authorizedVolume', e.target.value)}
              value={authorizedVolume ?? ''}
            />
          )}
          {authorizationType !== 'UNLIMITED' && authorizationType !== 'LIMITED' && (
            <TextField
              label="Renewal Volume (Gallons)"
              onChange={e => handleVolume('renewalVolume', e.target.value)}
              value={renewalVolume ?? ''}
            />
          )}
        </>
      )}
    </Box>
  );
};

VariableFields.propTypes = {
  authorizationBy: string.isRequired,
  params: shape({
    authorizationNumber: oneOfType([number, string]),
    authorizationType: string,
    authorizedVolume: oneOfType([number, string]),
    endDate: string,
    inventoryOwnerSelfAuthorizationIds: arrayOf(number),
    renewalVolume: oneOfType([number, string]),
    supplierIdName: oneOfType([number, string]),
  }),
  setHasUnsavedChanges: func.isRequired,
  setParams: func.isRequired,
  setWizardDetails: func.isRequired,
  viewById: number,
  viewByListFetched: bool,
  viewByListOptions: arrayOf(
    shape({
      label: string,
      value: number,
    })
  ),
};

VariableFields.defaultProps = {
  params: shape({
    authorizationNumber: null,
    authorizationType: '',
    authorizedVolume: null,
    endDate: '',
    inventoryOwnerSelfAuthorizationIds: [],
    renewalVolume: null,
    supplierIdName: '',
  }),
  viewById: null,
  viewByListFetched: false,
  viewByListOptions: [],
};

export default VariableFields;
